import React, { memo, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { SettingsActions, ConfigActions } from 'actions';
import { getLiteral } from 'utils/getLiteral';
import { NavigationTree } from 'containers/settings/deprecatedIntegrationsManagement/components/index';
import { ZoomIcon, MicrosoftTeamsList } from 'components/SvgIcons';
import { Switch } from 'hoi-poi-ui';
import './styles.scss';

const mapStateToProps = (state) => ({
    enableVideoCalls: state.config.permission.enableVideoCalls || false,
});

const mapDispatchToProps = (dispatch) => ({
    activateVideoCallProvider: bindActionCreators(SettingsActions, dispatch)
        .activateVideoCallProvider,
    updateSpecificPermissions: bindActionCreators(ConfigActions, dispatch)
        .updateSpecificPermissions,
});

const VideoCallIntegrationComponent = memo(
    ({
        provider,
        onReset,
        videoCallIntegrations,
        enableVideoCalls,
        activateVideoCallProvider,
        updateSpecificPermissions,
    }) => {
        const [activate, setActivate] = useState(() => provider?.data?.IsActive || false);

        const toggleActivation = useCallback(() => {
            if (!provider?.data?.ProviderId) return;
            const finalBool = !activate;

            activateVideoCallProvider(provider.data.ProviderId, finalBool)
                .then(() => {
                    if (finalBool && !enableVideoCalls) {
                        // activate the permission if necessary
                        updateSpecificPermissions([{ key: 'enableVideoCalls', value: true }]);
                    } else if (!finalBool && enableVideoCalls) {
                        // deactivate the permission if necessary
                        const allDeactivated = videoCallIntegrations
                            .filter((vc) => vc.id !== provider.id)
                            .every((vc) => !vc.active);
                        if (allDeactivated) {
                            updateSpecificPermissions([{ key: 'enableVideoCalls', value: false }]);
                        }
                    }
                    setActivate(finalBool);
                })
                .catch(console.error);
        }, [
            provider,
            activate,
            activateVideoCallProvider,
            enableVideoCalls,
            updateSpecificPermissions,
            videoCallIntegrations,
        ]);

        const boolFieldLabel = useMemo(() => {
            if (activate) return getLiteral('action_deactivate');
            else return getLiteral('action_activate');
        }, [activate]);

        const providerItems = useMemo(() => {
            switch (provider.id) {
                case 27:
                    return {
                        buttonFieldLabel: getLiteral('label_visit_zoom'),
                        icon: <ZoomIcon color="$fmWhite" />,
                        name: 'zoom',
                    };
                case 28:
                    return {
                        buttonFieldLabel: getLiteral('label_visit_teams'),
                        icon: <MicrosoftTeamsList color="$fmWhite" />,
                        name: 'teams',
                    };
                default:
                    return null;
            }
        }, [provider]);

        const videoCallIntegrationClass = useMemo(() => {
            let classes = ['video-call-integration'];
            if (providerItems?.name) {
                classes.push(`${providerItems?.name?.toLowerCase()}-integration`);
            }
            return classes;
        }, [providerItems]);

        return (
            <div className="detail-provider">
                <div className="top-panel">
                    <NavigationTree provider={provider} onReset={onReset} />
                    <div className={videoCallIntegrationClass.join(' ')}>
                        <div className="video-call-integration__header">
                            <div className="video-call-integration__header__icon">
                                {providerItems?.icon}
                            </div>
                            <div className="video-call-integration__header__text">
                                <div className="video-call-integration__header__title">
                                    {getLiteral(provider.nameLabel)}
                                </div>
                                <div className="video-call-integration__header__subtitle">
                                    {getLiteral(provider.descriptionShort)}
                                </div>
                            </div>
                            <div className="video-call-integration__header__activation-button">
                                <span>{boolFieldLabel}</span>
                                <Switch
                                    className="video-call-integration__header__activation-button__switch"
                                    onChange={toggleActivation}
                                    checked={activate}
                                />
                            </div>
                        </div>
                        <div className="video-call-integration__content">
                            <p>{getLiteral(provider.descriptionLong)}</p>
                            <a href={provider.website} target="_blank">
                                {providerItems?.buttonFieldLabel}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(VideoCallIntegrationComponent);
